/**
 * MechaGuard - WaveSystem (波次系统)
 *
 * 状态机：
 *   idle      → 未开始
 *   prep      → 备战倒计时（可提前召唤以获得金币奖励）
 *   spawning  → 按时间表投放敌人
 *   clearing  → 等待场上敌人被清空
 *   done      → 全部波次结束
 *
 * 波次数据由 buildWaves() 程序化生成：1~30 波难度递增，
 * 每 10 波为 BOSS 波。
 */

export const PREP_TIME = 8;

/** 波次生成的全局默认参数（运营视图可覆盖） */
export const WAVE_DEFAULTS = {
  hpGrowthPerWave: 0.10, // 每波生命成长倍率
  prepTime: 8,           // 备战倒计时（秒）
  bossEvery: 10,        // 每 N 波一个 BOSS 波
  bonusBase: 25,        // 通关基础奖励金币
  bonusPerWave: 8,      // 每波额外奖励（× 波号）
  intervalScale: 1,     // 刷怪间隔缩放（影响所有 group 的 interval/delay）
  totalWaves: 30,        // 总波数（覆盖各关卡自带的 totalWaves）
  enemyCountScale: 1,    // 每波各组怪物数量整体倍率（1=原样）
};

/**
 * 生成波次配置表
 * @param {number} [totalWaves=30]
 * @param {number} [hpScaleMul=1] 关卡难度倍率，直接乘在生命成长上
 * @param {Partial<typeof WAVE_DEFAULTS>} [opts] 运营覆盖层（合并自 WAVE_DEFAULTS）
 * @returns {{index:number, groups:{type:string,count:number,interval:number,delay:number}[], hpScale:number, bonus:number}[]}
 */
export function buildWaves(totalWaves = 30, hpScaleMul = 1, opts = {}) {
  const o = Object.assign({}, WAVE_DEFAULTS, opts);
  // 运营覆盖：总波数（优先级高于关卡自带 totalWaves）与出怪数量倍率
  const total = Number.isFinite(o.totalWaves) ? o.totalWaves : totalWaves;
  const scale = Number.isFinite(o.enemyCountScale) ? o.enemyCountScale : 1;
  // 出怪数量倍率：向上保底 1 只，四舍五入
  const cnt = (n) => Math.max(1, Math.round(n * scale));
  const waves = [];

  for (let w = 1; w <= total; w++) {
    /** @type {{type:string,count:number,interval:number,delay:number}[]} */
    const groups = [];

    // 生命成长倍率（再乘以关卡难度倍率）
    const hpScale = (1 + (w - 1) * o.hpGrowthPerWave) * hpScaleMul;
    const isBossWave = w % o.bossEvery === 0;
    const is = o.intervalScale; // 刷怪间隔缩放（缩写）

    if (isBossWave) {
      groups.push({
        type: 'grunt',
        count: cnt(6 + Math.floor(w * 0.4)),
        interval: 0.8 * is,
        delay: 0 * is,
      });
      groups.push({
        type: 'heavy',
        count: cnt(3 + Math.floor(w * 0.2)),
        interval: 1.2 * is,
        delay: 2 * is,
      });
      groups.push({
        type: 'boss',
        count: cnt(w >= 30 ? 2 : 1),
        interval: 3 * is,
        delay: 6 * is,
      });
    } else {
      groups.push({
        type: 'scout',
        count: cnt(4 + Math.floor(w * 0.55)),
        interval: 0.65 * is,
        delay: 0,
      });
      groups.push({
        type: 'grunt',
        count: cnt(2 + Math.floor(w * 0.45)),
        interval: 0.85 * is,
        delay: 1.6 * is,
      });

      if (w >= 4) {
        groups.push({
          type: 'heavy',
          count: cnt(1 + Math.floor(w * 0.22)),
          interval: 1.3 * is,
          delay: 3.2 * is,
        });
      }

      if (w >= 7) {
        groups.push({
          type: 'elite',
          count: cnt(1 + Math.floor(w * 0.16)),
          interval: 1.7 * is,
          delay: 5 * is,
        });
      }
    }

    waves.push({
      index: w,
      groups,
      hpScale,
      bonus: o.bonusBase + w * o.bonusPerWave,
    });
  }

  return waves;
}

export class WaveSystem {
  /**
   * @param {object} opts
   * @param {ReturnType<typeof buildWaves>} opts.waves
   * @param {(typeId:string, hpScale:number)=>void} opts.onSpawn
   * @param {(waveNumber:number)=>void} opts.onWaveStart
   * @param {(waveNumber:number, bonus:number)=>void} opts.onWaveComplete
   * @param {()=>void} opts.onAllComplete
   * @param {number} [opts.prepTime]
   */
  constructor({
    waves,
    onSpawn,
    onWaveStart,
    onWaveComplete,
    onAllComplete,
    prepTime = WAVE_DEFAULTS.prepTime,
  }) {
    this.waves = waves;
    this.onSpawn = onSpawn;
    this.onWaveStart = onWaveStart;
    this.onWaveComplete = onWaveComplete;
    this.onAllComplete = onAllComplete;
    this.prepTime = prepTime;

    /** -1 表示还没开始第一波 */
    this.waveIndex = -1;
    /** @type {'idle'|'prep'|'spawning'|'clearing'|'done'} */
    this.state = 'idle';

    this.prepTimer = 0;
    /** @type {{type:string,hpScale:number,time:number}[]} */
    this.queue = [];
    this.spawnCursor = 0;
    this.waveTime = 0;
    this.hpScale = 1;
  }

  /* ===================================================
   * 查询
   * =================================================== */

  get totalWaves() {
    return this.waves.length;
  }

  /** 供 HUD 显示的波次号（从 1 开始） */
  get currentWave() {
    return Math.max(0, this.waveIndex + 1);
  }

  get isRunning() {
    return this.state === 'prep' || this.state === 'spawning' || this.state === 'clearing';
  }

  /** 是否处于备战倒计时 */
  get isPreparing() {
    return this.state === 'prep';
  }

  get prepRemaining() {
    return this.state === 'prep' ? Math.max(0, this.prepTimer) : 0;
  }

  /** 即将开始的那一波是否为 BOSS 波（用于 UI 预警） */
  get nextIsBoss() {
    const upcoming = this.waveIndex + 2; // 下一波的波次号（从 1 开始）
    return upcoming >= 1 && upcoming <= this.waves.length && upcoming % 10 === 0;
  }

  /* ===================================================
   * 控制
   * =================================================== */

  /** 开局：进入第一波备战 */
  begin() {
    this.state = 'prep';
    this.prepTimer = this.prepTime;
    this.waveIndex = -1;
  }

  /**
   * 提前召唤下一波
   * @returns {number} 被跳过的剩余秒数（0 表示当前不可召唤）
   */
  callNextWave() {
    if (this.state !== 'prep') return 0;
    const remaining = Math.max(0, this.prepTimer);
    this.prepTimer = 0;
    return remaining;
  }

  /* ===================================================
   * 主循环
   * =================================================== */

  /**
   * @param {number} dt
   * @param {number} aliveEnemies 场上存活敌人数
   */
  update(dt, aliveEnemies) {
    switch (this.state) {
      case 'prep': {
        this.prepTimer -= dt;
        if (this.prepTimer <= 0) {
          this.prepTimer = 0;
          this._startNextWave();
        }
        break;
      }

      case 'spawning': {
        this.waveTime += dt;

        while (
          this.spawnCursor < this.queue.length &&
          this.queue[this.spawnCursor].time <= this.waveTime
        ) {
          const item = this.queue[this.spawnCursor];
          this.spawnCursor += 1;
          this.onSpawn(item.type, item.hpScale);
        }

        if (this.spawnCursor >= this.queue.length) {
          this.state = 'clearing';
        }
        break;
      }

      case 'clearing': {
        if (aliveEnemies <= 0) {
          const finished = this.currentWave;
          const wave = this.waves[this.waveIndex];
          this.onWaveComplete(finished, wave ? wave.bonus : 0);

          if (this.waveIndex + 1 >= this.waves.length) {
            this.state = 'done';
            this.onAllComplete();
          } else {
            this.state = 'prep';
            this.prepTimer = this.prepTime;
          }
        }
        break;
      }

      default:
        break;
    }
  }

  /* ===================================================
   * 内部
   * =================================================== */

  _startNextWave() {
    this.waveIndex += 1;

    const wave = this.waves[this.waveIndex];
    if (!wave) {
      this.state = 'done';
      return;
    }

    this.hpScale = wave.hpScale;

    // 展开投放时间表
    const queue = [];
    for (let g = 0; g < wave.groups.length; g++) {
      const group = wave.groups[g];
      for (let i = 0; i < group.count; i++) {
        queue.push({
          type: group.type,
          hpScale: wave.hpScale,
          time: group.delay + i * group.interval,
        });
      }
    }

    queue.sort((a, b) => a.time - b.time);

    this.queue = queue;
    this.spawnCursor = 0;
    this.waveTime = 0;
    this.state = 'spawning';

    this.onWaveStart(this.currentWave);
  }
}
